import { createFeature, createReducer, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { NotificationTemplate } from '@modules/notifications/notification-template/entities/notification-template.model';
import { NotificationTemplateActions } from './notification-template.actions';

export interface NotificationTemplateState extends EntityState<NotificationTemplate> {
  loading: boolean;
  error: unknown | null;
  selectedUuid: string | null;
}

export const notificationTemplateAdapter =
  createEntityAdapter<NotificationTemplate>({
    selectId: (tpl) => tpl.uuid,
    sortComparer: (a, b) => a.name.localeCompare(b.name),
  });

const initialState: NotificationTemplateState =
  notificationTemplateAdapter.getInitialState({
    loading: false,
    error: null,
    selectedUuid: null,
  });

const reducer = createReducer(
  initialState,

  // ---------- Load One ----------
  on(NotificationTemplateActions.loadOne, (state, { uuid }) => ({
    ...state,
    loading: true,
    error: null,
    selectedUuid: uuid,
  })),

  on(NotificationTemplateActions.loadOneSuccess, (state, { entity }) =>
    notificationTemplateAdapter.upsertOne(entity, {
      ...state,
      loading: false,
      error: null,
    })
  ),

  on(NotificationTemplateActions.loadOneFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // ---------- Update ----------
  on(NotificationTemplateActions.update, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(NotificationTemplateActions.updateSuccess, (state, { entity }) =>
    notificationTemplateAdapter.updateOne(
      {
        id: entity.uuid,        // <- adapter key is uuid
        changes: entity,
      },
      {
        ...state,
        loading: false,
        error: null,
      }
    )
  ),

  on(NotificationTemplateActions.updateFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  }))
);

// Feature registration
export const notificationTemplateFeature = createFeature({
  name: 'notificationTemplates',
  reducer,
});

// Adapter selectors
export const {
  selectAll: selectAllNotificationTemplates,
  selectEntities: selectNotificationTemplateEntities,
  selectIds: selectNotificationTemplateIds,
  selectTotal: selectNotificationTemplateTotal,
} = notificationTemplateAdapter.getSelectors(
  notificationTemplateFeature.selectNotificationTemplatesState
);
